import { Controller } from '@hotwired/stimulus';

const SCRIPT_URL = 'https://cdn.applied.guide/artifacts/applied-chat.umd.js';
const SHOW_WAIT_MS = 3000;
const SHOW_POLL_MS = 50;

let widgetLoad = null;

/**
 * Mounts the widget element from the template and loads its script, once per page.
 * The pending load is shared at module level, so repeat clicks and Turbo visits never
 * load the widget twice. The CDN build is unversioned, so every call into its window
 * API is guarded.
 *
 * @param {HTMLTemplateElement} template
 * @returns {Promise<void>} resolves when <applied-chat> is defined
 */
const loadWidget = (template) => {
  if (widgetLoad) return widgetLoad;

  widgetLoad = new Promise((resolve, reject) => {
    if (!document.querySelector('applied-chat')) {
      document.body.appendChild(template.content.cloneNode(true));
    }

    if (!customElements.get('applied-chat')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = SCRIPT_URL;
      script.onerror = () => {
        widgetLoad = null;
        script.remove();
        reject(new Error('Applied Labs chat failed to load'));
      };
      document.head.appendChild(script);
    }

    customElements.whenDefined('applied-chat').then(resolve);
  });

  return widgetLoad;
};

/**
 * The widget exposes window.applied in connectedCallback, after the element upgrades.
 *
 * @returns {Promise<Function|null>} window.applied.show, or null if it never appears
 */
const waitForShow = async () => {
  const deadline = Date.now() + SHOW_WAIT_MS;

  while (Date.now() < deadline) {
    if (typeof window.applied?.show === 'function') return window.applied.show;
    await new Promise((resolve) => setTimeout(resolve, SHOW_POLL_MS));
  }

  return null;
};

/**
 * Stands in for the Applied Labs launcher until the visitor asks for chat.
 *
 * The widget mints an anonymous contact and writes ID-named storage keys as soon as
 * it mounts, so it stays out of the page until the first click. That click loads it,
 * opens the window, and hands the launcher over to the widget's own button.
 */
export default class extends Controller {
  static targets = ['button', 'widget'];

  connect() {
    if (document.querySelector('applied-chat')) this.buttonTarget.hidden = true;
  }

  async open() {
    if (this.loading) return;
    this.loading = true;

    try {
      await loadWidget(this.widgetTarget);
      const show = await waitForShow();
      show?.();
      this.buttonTarget.hidden = true;
    } catch (_e) {
    } finally {
      this.loading = false;
    }
  }
}
